import { useEffect, useState, type ReactElement } from 'react'
import {
  listFamilyLeave,
  cancelEduLeave,
  type EduLeaveRequest,
  type EduLeaveStatus,
} from '../../services/eduLeaveService'
import { FamilyCard, Button, StatusChip, ConfirmActionModal, type StatusPillTone } from '../Common'
import { mapEduServerError } from '../Education/classesBuilderHelpers'
import { useNls } from '../../nls/nlsProvider'
import type { NlsKey } from '../../nls/strings_nls'
import { formatCalendarDate } from '../../utils/eduFamilyFormat'
import { FamilyLeaveDialog } from './FamilyLeaveDialog'
import './familyLeave.css'

export interface FamilyLeavePanelProps {
  workspaceId: string
  learnerId: string
  frozen?: boolean
  onBack?: () => void
}

const leaveStatusTone = (status: EduLeaveStatus): StatusPillTone => {
  switch (status) {
    case 'APPROVED':
      return 'success'
    case 'PENDING':
      return 'warning'
    case 'DECLINED':
    case 'REVOKED':
      return 'danger'
    case 'CANCELLED':
    default:
      return 'neutral'
  }
}

const leaveStatusLabel = (status: EduLeaveStatus, t: (k: NlsKey) => string): string => {
  switch (status) {
    case 'APPROVED':
      return t('NLSFamilyLeaveStatusApproved')
    case 'PENDING':
      return t('NLSFamilyLeaveStatusPending')
    case 'DECLINED':
      return t('NLSFamilyLeaveStatusDeclined')
    case 'CANCELLED':
      return t('NLSFamilyLeaveStatusCancelled')
    case 'REVOKED':
      return t('NLSFamilyLeaveStatusRevoked')
    default:
      return status
  }
}

export const FamilyLeavePanel = ({
  workspaceId,
  learnerId,
  frozen = false,
  onBack,
}: FamilyLeavePanelProps): ReactElement => {
  const { t, locale } = useNls()
  const [reloadSeq, setReloadSeq] = useState<number>(0)
  const queryKey = `${workspaceId}_${learnerId}_${reloadSeq}`

  const [fetchState, setFetchState] = useState<{
    key: string
    requests: EduLeaveRequest[] | null
    error: string | null
  }>({
    key: '',
    requests: null,
    error: null,
  })

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [cancelTarget, setCancelTarget] = useState<EduLeaveRequest | null>(null)
  const [isCancelling, setIsCancelling] = useState<boolean>(false)

  useEffect(() => {
    if (frozen) return
    let active = true

    listFamilyLeave({ workspaceId, learnerId })
      .then((result) => {
        if (!active) return
        setFetchState({ key: queryKey, requests: result.requests, error: null })
      })
      .catch((err: unknown) => {
        if (!active) return
        setFetchState({ key: queryKey, requests: null, error: mapEduServerError(err, t) })
      })

    return () => {
      active = false
    }
  }, [workspaceId, learnerId, frozen, reloadSeq, queryKey, t])

  const error = fetchState.key === queryKey ? fetchState.error : null
  const loading = !frozen && fetchState.key !== queryKey && !error
  const requests = fetchState.key === queryKey ? fetchState.requests : null

  const handleConfirmCancel = async () => {
    if (!cancelTarget || !cancelTarget.id) return
    setIsCancelling(true)
    try {
      await cancelEduLeave({
        workspaceId,
        requestId: cancelTarget.id,
      })
      setCancelTarget(null)
      setReloadSeq((s) => s + 1)
    } catch (err: unknown) {
      setFetchState((prev) => ({
        ...prev,
        error: mapEduServerError(err, t),
      }))
      setCancelTarget(null)
    } finally {
      setIsCancelling(false)
    }
  }

  if (frozen) {
    return (
      <div className="familyLeavePanel">
        {onBack && (
          <div className="familyLeavePanel__nav">
            <Button variant="tertiary" size="sm" onClick={onBack}>
              {t('NLSFamilyLeaveBack')}
            </Button>
          </div>
        )}
        <FamilyCard title={t('NLSFamilyNoticeHeading')}>
          <div className="familyLeavePanel__frozen">{t('NLSFamilyFrozenNotice')}</div>
        </FamilyCard>
      </div>
    )
  }

  if (loading && !requests) {
    return (
      <div className="familyLeavePanel">
        {onBack && (
          <div className="familyLeavePanel__nav">
            <Button variant="tertiary" size="sm" onClick={onBack}>
              {t('NLSFamilyLeaveBack')}
            </Button>
          </div>
        )}
        <div className="familyLeavePanel__loading" aria-label={t('NLSFamilyAriaLoading')}>
          {t('NLSFamilyLoading')}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="familyLeavePanel">
        {onBack && (
          <div className="familyLeavePanel__nav">
            <Button variant="tertiary" size="sm" onClick={onBack}>
              {t('NLSFamilyLeaveBack')}
            </Button>
          </div>
        )}
        <FamilyCard title={t('NLSFamilyErrorTitle')}>
          <div className="familyLeavePanel__error">{error}</div>
          <div className="familyLeavePanel__retry">
            <Button variant="secondary" size="sm" onClick={() => setReloadSeq((s) => s + 1)}>
              {t('NLSFamilyRetry')}
            </Button>
          </div>
        </FamilyCard>
      </div>
    )
  }

  const items = requests ?? []

  return (
    <div className="familyLeavePanel">
      {onBack && (
        <div className="familyLeavePanel__nav">
          <Button variant="tertiary" size="sm" onClick={onBack}>
            {t('NLSFamilyLeaveBack')}
          </Button>
        </div>
      )}

      <FamilyCard
        title={t('NLSFamilyLeaveHeading')}
        actions={
          <Button variant="primary" size="sm" onClick={() => setIsDialogOpen(true)}>
            {t('NLSFamilyLeaveAskButton')}
          </Button>
        }
      >
        {items.length === 0 ? (
          <div className="familyLeavePanel__empty">{t('NLSFamilyLeaveEmpty')}</div>
        ) : (
          <div className="familyLeavePanel__list">
            {items.map((req, idx) => {
              const reqKey = req.id || `req_${idx}`
              const isPending = req.status === 'PENDING'
              const datesFormatted = `${formatCalendarDate(req.fromDate, locale)} – ${formatCalendarDate(req.toDate, locale)}`

              return (
                <div key={reqKey} className="familyLeavePanel__row">
                  <div className="familyLeavePanel__rowMain">
                    <div className="familyLeavePanel__rowDates">{datesFormatted}</div>
                    <div className="familyLeavePanel__rowReason">{req.reason}</div>
                    <div className="familyLeavePanel__statusLine">
                      <StatusChip tone={leaveStatusTone(req.status)} size="sm">
                        {leaveStatusLabel(req.status, t)}
                      </StatusChip>
                      {req.decisionNote && (
                        <div className="familyLeavePanel__decisionNote">
                          {t('NLSFamilyLeaveDecisionNote', { note: req.decisionNote })}
                        </div>
                      )}
                    </div>
                  </div>

                  {isPending && (
                    <div className="familyLeavePanel__rowAction">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setCancelTarget(req)}
                      >
                        {t('NLSFamilyLeaveCancelAction')}
                      </Button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </FamilyCard>

      <FamilyLeaveDialog
        open={isDialogOpen}
        workspaceId={workspaceId}
        learnerId={learnerId}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={() => setReloadSeq((s) => s + 1)}
      />

      <ConfirmActionModal
        open={Boolean(cancelTarget)}
        title={t('NLSFamilyLeaveCancelConfirmTitle')}
        body={t('NLSFamilyLeaveCancelConfirmBody')}
        confirmLabel={t('NLSFamilyLeaveCancelConfirmButton')}
        danger={true}
        busy={isCancelling}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleConfirmCancel}
      />
    </div>
  )
}
